import React from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { CommunityAssessmentData } from '@/types/entities/clients/assessment';
import { Smartphone, Monitor, Wifi, Database, Shield } from 'lucide-react';

interface TechnologyAssessmentProps {
  data: Partial<CommunityAssessmentData>;
  onUpdate: (data: Partial<CommunityAssessmentData>) => void;
  tier: 'silver' | 'gold' | 'custom';
}

export const Step_4_TechnologyAssessment: React.FC<TechnologyAssessmentProps> = ({
  data,
  onUpdate,
}) => {
  const updateTechField = (field: string, value: string | string[]) => {
    onUpdate({
      ...data,
      technologyAssessment: {
        ...data.technologyAssessment,
        [field]: value,
      },
    });
  };

  const updateSoftwareList = (software: string, checked: boolean) => {
    const currentSoftware = data.technologyAssessment?.currentSoftware || [];
    const updated = checked
      ? [...currentSoftware, software]
      : currentSoftware.filter((s) => s !== software);
    updateTechField('currentSoftware', updated);
  };

  const updateCommunicationMethods = (method: string, checked: boolean) => {
    const currentMethods = data.technologyAssessment?.communicationMethods || [];
    const updated = checked
      ? [...currentMethods, method]
      : currentMethods.filter((m) => m !== method);
    updateTechField('communicationMethods', updated);
  };

  const updateVotingMethods = (method: string, checked: boolean) => {
    const currentMethods = data.technologyAssessment?.votingMethods || [];
    const updated = checked
      ? [...currentMethods, method]
      : currentMethods.filter((m) => m !== method);
    updateTechField('votingMethods', updated);
  };

  const updateFinancialManagement = (tool: string, checked: boolean) => {
    const currentTools = data.technologyAssessment?.financialManagement || [];
    const updated = checked ? [...currentTools, tool] : currentTools.filter((t) => t !== tool);
    updateTechField('financialManagement', updated);
  };

  const updateMaintenanceTracking = (tool: string, checked: boolean) => {
    const currentTools = data.technologyAssessment?.maintenanceTracking || [];
    const updated = checked ? [...currentTools, tool] : currentTools.filter((t) => t !== tool);
    updateTechField('maintenanceTracking', updated);
  };

  const currentSoftwareOptions = [
    'AppFolio',
    'Buildium',
    'TOPS [ONE]',
    'Condo Control',
    'TownSq',
    'HOALife',
    'QuickBooks',
    'Excel/Google Sheets',
    'Email only',
    'No current software',
    'Other',
  ];

  const communicationMethodOptions = [
    'Email newsletters',
    'Text/SMS messages',
    'Physical bulletin boards',
    'Website announcements',
    'Social media (Facebook, etc.)',
    'Mobile app notifications',
    'Printed letters/flyers',
    'Phone calls',
    'Community meetings only',
  ];

  const votingMethodOptions = [
    'Paper ballots at meetings',
    'Email voting',
    'Mailed paper ballots',
    'Online voting platform',
    'Proxy voting',
    'Show of hands at meetings',
    'No formal voting system',
  ];

  const financialManagementOptions = [
    'QuickBooks',
    'Excel spreadsheets',
    'Professional accounting software',
    'HOA management platform',
    'Bank online tools only',
    'Manual/paper records',
    'Accountant handles everything',
  ];

  const maintenanceTrackingOptions = [
    'Email requests',
    'Phone calls only',
    'Paper work orders',
    'Spreadsheet tracking',
    'Property management software',
    'No formal tracking system',
    'Vendor manages their own tracking',
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Technology & Systems Assessment
        </h3>
        <p className="text-gray-600">
          Understanding your current technology helps us plan the smoothest transition and
          integration
        </p>
      </div>

      <div className="space-y-6">
        {/* Current Software */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Monitor className="w-5 h-5 text-blue-600" />
              Current Software & Platforms
            </CardTitle>
            <CardDescription>
              What software does your community currently use? (Select all that apply)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-3">
              {currentSoftwareOptions.map((software) => (
                <div key={software} className="flex items-center space-x-2">
                  <Checkbox
                    id={`software-${software}`}
                    checked={
                      data.technologyAssessment?.currentSoftware?.includes(software) || false
                    }
                    onCheckedChange={(checked) => updateSoftwareList(software, checked as boolean)}
                  />
                  <Label htmlFor={`software-${software}`} className="text-sm">
                    {software}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Communication Methods */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-green-600" />
              Communication Methods
            </CardTitle>
            <CardDescription>
              How does your community currently communicate with residents?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-3">
              {communicationMethodOptions.map((method) => (
                <div key={method} className="flex items-center space-x-2">
                  <Checkbox
                    id={`comm-method-${method}`}
                    checked={
                      data.technologyAssessment?.communicationMethods?.includes(method) || false
                    }
                    onCheckedChange={(checked) =>
                      updateCommunicationMethods(method, checked as boolean)
                    }
                  />
                  <Label htmlFor={`comm-method-${method}`} className="text-sm">
                    {method}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Voting Methods */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-600" />
              Current Voting & Decision Making
            </CardTitle>
            <CardDescription>
              How does your community currently conduct votes and make decisions?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-3">
              {votingMethodOptions.map((method) => (
                <div key={method} className="flex items-center space-x-2">
                  <Checkbox
                    id={`voting-${method}`}
                    checked={data.technologyAssessment?.votingMethods?.includes(method) || false}
                    onCheckedChange={(checked) => updateVotingMethods(method, checked as boolean)}
                  />
                  <Label htmlFor={`voting-${method}`} className="text-sm">
                    {method}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Financial Management */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Database className="w-5 h-5 text-orange-600" />
              Financial Management Tools
            </CardTitle>
            <CardDescription>
              What tools do you use for budgeting, accounting, and financial tracking?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-3">
              {financialManagementOptions.map((tool) => (
                <div key={tool} className="flex items-center space-x-2">
                  <Checkbox
                    id={`financial-tool-${tool}`}
                    checked={
                      data.technologyAssessment?.financialManagement?.includes(tool) || false
                    }
                    onCheckedChange={(checked) =>
                      updateFinancialManagement(tool, checked as boolean)
                    }
                  />
                  <Label htmlFor={`financial-tool-${tool}`} className="text-sm">
                    {tool}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Maintenance Tracking */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Wifi className="w-5 h-5 text-red-600" />
              Maintenance & Work Order Tracking
            </CardTitle>
            <CardDescription>
              How do you currently handle maintenance requests and work orders?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-3">
              {maintenanceTrackingOptions.map((tool) => (
                <div key={tool} className="flex items-center space-x-2">
                  <Checkbox
                    id={`maintenance-tool-${tool}`}
                    checked={
                      data.technologyAssessment?.maintenanceTracking?.includes(tool) || false
                    }
                    onCheckedChange={(checked) =>
                      updateMaintenanceTracking(tool, checked as boolean)
                    }
                  />
                  <Label htmlFor={`maintenance-tool-${tool}`} className="text-sm">
                    {tool}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Technology Comfort Level */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Technology Comfort Level</CardTitle>
            <CardDescription>
              How comfortable is your community (board and residents) with adopting new technology?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div>
              <Label htmlFor="technologyComfort">Overall Technology Comfort Level</Label>
              <Select
                value={data.technologyAssessment?.technologyComfort || ''}
                onValueChange={(value) => updateTechField('technologyComfort', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select comfort level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">
                    Low - Prefer traditional methods, slow to adopt new technology
                  </SelectItem>
                  <SelectItem value="medium">
                    Medium - Open to technology but need support and training
                  </SelectItem>
                  <SelectItem value="high">
                    High - Tech-savvy community, eager to adopt new solutions
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Technology Readiness Insight */}
      {data.technologyAssessment?.technologyComfort && (
        <Card
          className={
            data.technologyAssessment.technologyComfort === 'high'
              ? 'bg-green-50 border-green-200'
              : data.technologyAssessment.technologyComfort === 'medium'
                ? 'bg-yellow-50 border-yellow-200'
                : 'bg-red-50 border-red-200'
          }
        >
          <CardContent className="pt-6">
            <div className="text-center">
              <h4
                className={`font-semibold mb-2 ${
                  data.technologyAssessment.technologyComfort === 'high'
                    ? 'text-green-800'
                    : data.technologyAssessment.technologyComfort === 'medium'
                      ? 'text-yellow-800'
                      : 'text-red-800'
                }`}
              >
                Implementation Approach
              </h4>
              <p
                className={`text-sm ${
                  data.technologyAssessment.technologyComfort === 'high'
                    ? 'text-green-700'
                    : data.technologyAssessment.technologyComfort === 'medium'
                      ? 'text-yellow-700'
                      : 'text-red-700'
                }`}
              >
                {data.technologyAssessment.technologyComfort === 'high' &&
                  'Great! Your tech-savvy community will allow for a faster rollout with advanced features from day one.'}
                {data.technologyAssessment.technologyComfort === 'medium' &&
                  "Perfect! We'll provide comprehensive training and a gradual rollout to ensure everyone is comfortable."}
                {data.technologyAssessment.technologyComfort === 'low' &&
                  'No problem! We specialize in helping traditional communities transition smoothly with extra support and training.'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          <strong>Why we need this information:</strong>
        </p>
        <ul className="text-sm text-blue-700 mt-2 space-y-1">
          <li>• Plan data migration from your current systems</li>
          <li>• Design training programs appropriate for your technology comfort level</li>
          <li>• Identify integration opportunities with tools you want to keep</li>
          <li>• Customize the rollout timeline based on your community's adoption readiness</li>
          <li>• Ensure we don't disrupt critical operations during transition</li>
        </ul>
      </div>
    </div>
  );
};
